/**
 * src/shell/agents/components/widgets/AccessTimelineWidget.tsx
 *
 * Chronological Event & Door Access Stream.
 * Displays door unlock assertions, auth methods, and status.
 */

import React, { useState, useEffect } from 'react';
import { IGenUIWidgetSpec } from '../../types/genui-types';
import { canvasTelemetryConnector } from '../../services/CanvasTelemetryConnector';

export interface AccessTimelineWidgetProps {
  spec: IGenUIWidgetSpec;
}

export const AccessTimelineWidget: React.FC<AccessTimelineWidgetProps> = ({ spec }) => {
  const [data, setData] = useState<Record<string, unknown>>({});

  useEffect(() => {
    return canvasTelemetryConnector.subscribe(
      spec.dataSource.endpoint,
      spec.dataSource.refreshIntervalMs || 1500,
      (payload) => setData(payload)
    );
  }, [spec.dataSource.endpoint, spec.dataSource.refreshIntervalMs]);

  const events = (data.doorEvents as Array<{ id: string; door: string; user: string; authMethod: string; status: string; time: string }>) || [
    { id: 'evt_1', door: 'Server Room 4B', user: 'admin_security', authMethod: 'BLE_PROXIMITY', status: 'GRANTED', time: 'Just now' },
    { id: 'evt_2', door: 'Main Entrance Gate', user: 'operator_dan', authMethod: 'NFC_BADGE', status: 'GRANTED', time: '2m ago' },
    { id: 'evt_3', door: 'Vault Room Alpha', user: 'guest_unauth', authMethod: 'KEYPAD', status: 'DENIED', time: '5m ago' },
  ];

  return (
    <div
      data-testid={`timeline-widget-${spec.id}`}
      style={{
        background: 'rgba(15, 23, 42, 0.75)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: '14px',
        padding: '16px',
        color: '#f8fafc',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <div>
          <div style={{ fontSize: '12px', fontWeight: 800 }}>{spec.title}</div>
          <div style={{ fontSize: '10px', color: '#94a3b8' }}>{spec.description}</div>
        </div>
        <span style={{ fontSize: '10px', color: '#f59e0b', fontWeight: 800 }}>
          LIVE FEED
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, overflowY: 'auto' }}>
        {events.map((evt) => (
          <div
            key={evt.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '6px 10px',
              background: 'rgba(0, 0, 0, 0.25)',
              borderRadius: '8px',
              fontSize: '11px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span
                style={{
                  fontSize: '8px',
                  fontWeight: 800,
                  padding: '1px 4px',
                  borderRadius: '3px',
                  background: evt.status === 'GRANTED' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                  color: evt.status === 'GRANTED' ? '#10b981' : '#ef4444',
                }}
              >
                {evt.status}
              </span>
              <strong style={{ color: '#e2e8f0' }}>{evt.door}</strong>
              <span style={{ color: '#94a3b8' }}>({evt.user})</span>
            </div>
            <span style={{ color: '#64748b', fontSize: '10px' }}>{evt.time}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
